import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import i18next from 'i18next';
import {
  Modal,
  Box,
  IconButton,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Divider,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DownloadIcon from '@mui/icons-material/Download';
import FileUploadIcon from '@mui/icons-material/FileUpload';
import { useLocalizedTag } from '../../context/LocalizedTagContext';
import { useSettings } from '../../context/SettingsContext';

// Function to save translations to localStorage
const saveTranslationsToStorage = (language, translations) => {
  try {
    localStorage.setItem(`translations_${language}`, JSON.stringify(translations));
  } catch (err) {
    console.error('Failed to save translations to localStorage:', err);
  }
};

// Function to load translations from localStorage
const loadTranslationsFromStorage = (language) => {
  try {
    const stored = localStorage.getItem(`translations_${language}`);
    return stored ? JSON.parse(stored) : null;
  } catch (err) {
    console.error('Failed to load translations from localStorage:', err);
    return null;
  }
};

const LocalizationModal = ({ isOpen, onClose }) => {
  const { t } = useTranslation();
  const { LocalizedTag, language } = useLocalizedTag();
  const { settings } = useSettings();
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const namespaces = [
    'translation',
    'vehicle',
    'schedule',
    'stop',
    'route',
    'user',
    'manageData',
    'alert',
  ];

  // Load saved translations on component mount
  useEffect(() => {
    const savedTranslations = loadTranslationsFromStorage('ud');
    if (savedTranslations) {
      Object.entries(savedTranslations).forEach(([namespace, content]) => {
        i18next.addResourceBundle('ud', namespace, content, true, true);
      });
      if (language === 'ud') {
        i18next.changeLanguage('ud');
      }
    }
  }, [language]);

  const modalStyle = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: '100%',
    maxWidth: 500,
    bgcolor: 'background.paper',
    boxShadow: 24,
    borderRadius: 2,
    p: 0,
    outline: 'none',
  };
  const headerStyle = {
    backgroundColor: 'primary.main',
    color: 'white',
    py: 2,
    px: 3,
    borderTopLeftRadius: 8,
    borderTopRightRadius: 8,
    position: 'relative',
  };
  const contentStyle = {
    p: 3,
  };

  const handleDownloadKeys = () => {
    try {
      const allKeys = {};
      namespaces.forEach((namespace) => {
        const resources = i18next.getResourceBundle(language, namespace);
        if (resources) {
          Object.keys(resources).forEach((key) => {
            allKeys[`${namespace}:${key}`] = '';
          });
        }
      });

      const blob = new Blob([JSON.stringify(allKeys, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${language}-all-keys.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      setError(t('failed_to_download_keys'));
    }
  };

  const parseFileContent = (content, fileType) => {
    try {
      switch (fileType) {
        case 'application/json':
          const translations = JSON.parse(content);
          const isConsolidated = Object.keys(translations).some((key) =>
            namespaces.includes(key)
          );
          return { isConsolidated, translations };
        case 'text/plain':
          return content.split('\n').reduce((acc, line) => {
            const [key, value] = line.split('=').map((str) => str.trim());
            if (key && value) {
              acc[key] = value;
            }
            return acc;
          }, {});
        case 'text/xml':
          const parser = new DOMParser();
          const xmlDoc = parser.parseFromString(content, 'text/xml');
          const entries = xmlDoc.getElementsByTagName('entry');
          return Array.from(entries).reduce((acc, entry) => {
            const key = entry.getAttribute('key');
            const value = entry.textContent;
            if (key && value) {
              acc[key] = value;
            }
            return acc;
          }, {});
        default:
          throw new Error('Unsupported file type');
      }
    } catch (err) {
      throw new Error('Failed to parse file content');
    }
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    setError('');
    setSuccess(false);

    if (!file) return;

    try {
      const content = await file.text();
      const { isConsolidated, translations } = parseFileContent(content, file.type);

      let processedTranslations = {};

      if (isConsolidated) {
        processedTranslations = translations;
        Object.entries(translations).forEach(([namespace, content]) => {
          i18next.addResourceBundle('ud', namespace, content, true, true);
        });
      } else {
        processedTranslations = Object.entries(translations).reduce((acc, [key, value]) => {
          const [namespace, actualKey] = key.split(':');
          if (!acc[namespace]) acc[namespace] = {};
          acc[namespace][actualKey] = value;
          return acc;
        }, {});

        Object.entries(processedTranslations).forEach(([namespace, content]) => {
          i18next.addResourceBundle('ud', namespace, content, true, true);
        });
      }

      saveTranslationsToStorage('ud', processedTranslations);

      setSuccess(true);
      setTimeout(() => {
        onClose();
        setSuccess(false);
        if (language === 'ud') {
          window.location.reload();
        }
      }, 1500);
    } catch (err) {
      setError(t('failed_to_process_file'));
    }

    event.target.value = '';
  };

  return (
    <Modal
      open={isOpen}
      onClose={onClose}
      aria-labelledby="upload-translations-modal"
      aria-describedby="modal-to-upload-translation-files"
    >
      <Paper sx={modalStyle}>
        {/* Header */}
        <Box sx={headerStyle}>
          <LocalizedTag
            tag="h2"
            textKey="upload_translations"
            style={{ margin: 0, fontSize: '1.5rem', fontWeight: 500 }}
          />
          <IconButton
            aria-label="close"
            onClick={onClose}
            sx={{
              position: 'absolute',
              right: 8,
              top: '50%',
              transform: 'translateY(-50%)',
              color: 'white',
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>

        {/* Content */}
        <Box sx={contentStyle}>
          {/* Supported Formats */}
          <LocalizedTag
            tag="p"
            textKey="supported_formats"
            style={{ marginBottom: '8px', color: 'text.secondary' }}
          />
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <LocalizedTag
                    tag="th"
                    textKey="format"
                    style={{ padding: '8px', fontWeight: 'bold' }}
                  />
                  <LocalizedTag
                    tag="th"
                    textKey="description"
                    style={{ padding: '8px', fontWeight: 'bold' }}
                  />
                </TableRow>
              </TableHead>
              <TableBody>
                <TableRow>
                  <LocalizedTag
                    tag={TableCell}
                    textKey="json_format"
                    style={{ padding: '8px' }}
                  />
                  <LocalizedTag
                    tag={TableCell}
                    textKey="json_description"
                    style={{ padding: '8px' }}
                  />
                </TableRow>
                <TableRow>
                  <LocalizedTag
                    tag={TableCell}
                    textKey="text_format"
                    style={{ padding: '8px' }}
                  />
                  <LocalizedTag
                    tag={TableCell}
                    textKey="text_description"
                    style={{ padding: '8px' }}
                  />
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>

          <Divider sx={{ my: 2 }} />

          {/* Upload and Download Section */}
          <Box sx={{ display: 'flex', gap: 2, mb: 2, '& .MuiButton-root': { minWidth: 'fit-content' } }}>
            <Button
              variant="contained"
              component="label"
              sx={{ flex: 1 }}
              startIcon={<FileUploadIcon />}
            >
              <LocalizedTag tag="span" textKey="select_file" />
              <input
                type="file"
                accept=".json,.txt"
                onChange={handleFileUpload}
                hidden
              />
            </Button>

            <Button
              variant="outlined"
              startIcon={<DownloadIcon />}
              onClick={handleDownloadKeys}
              sx={{ whiteSpace: 'nowrap' }}
            >
              <LocalizedTag tag="span" textKey="download_keys" />
            </Button>
          </Box>

          {/* Status Messages */}
          {error && (
            <LocalizedTag
              tag="div"
              text={error}
              style={{
                marginTop: '16px',
                padding: '8px',
                backgroundColor: 'error.light',
                borderRadius: '4px',
                color: 'error.main',
                opacity: 0.9,
              }}
            />
          )}

          {success && (
            <LocalizedTag
              tag="div"
              textKey="translations_updated"
              style={{
                marginTop: '16px',
                padding: '8px',
                backgroundColor: 'success.light',
                borderRadius: '4px',
                color: 'success.main',
                opacity: 0.9,
              }}
            />
          )}
        </Box>
      </Paper>
    </Modal>
  );
};

export default LocalizationModal;